import {
  useCallback,
  useEffect,
  useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import Autocomplete from '../components/SearchableAutocomplete';
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SidebarLayout from "../components/sidebarLayout";
import {
  createDepartment,
  deleteDepartment,
  getAllDepartments,
  updateDepartment,
  updateUserDepartments,
} from "../APIs/DepartmentsAPI";
import { getAllUtenti } from "../APIs/UsersAPI";
import { useSnackbar } from "../contexts/SnackBarContext";
import { localizedErrorMessage } from "../utils/italianErrorMessage";
import { useTranslation } from "react-i18next";

const EMPTY_FORM = {
  name: "",
  description: "",
  active: true,
};

function userLabel(user, t) {
  return `${user?.name || ""} ${user?.surname || ""}`.trim() || user?.email || t("departments.userFallback", { id: user?.id });
}

export default function GestioneRepartiPage() {
  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const tenantType = String(localStorage.getItem("tenantType") || "").toUpperCase();
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [departmentData, userData] = await Promise.all([
        getAllDepartments(token, tenant),
        getAllUtenti(token, tenant),
      ]);
      setDepartments(Array.isArray(departmentData) ? departmentData : []);
      setUsers(Array.isArray(userData) ? userData : []);
    } catch (err) {
      setError(localizedErrorMessage(err, t("departments.loadError")));
    } finally {
      setLoading(false);
    }
  }, [tenant, token, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      description: item.description || "",
      active: item.active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showSnackbar(t("departments.validation.nameRequired"), "warning");
      return;
    }

    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        active: Boolean(form.active),
      };
      if (editing?.id) {
        await updateDepartment(editing.id, payload, token, tenant);
        showSnackbar(t("departments.updated"), "success");
      } else {
        await createDepartment(payload, token, tenant);
        showSnackbar(t("departments.created"), "success");
      }
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      showSnackbar(err?.message || t("departments.saveError"), "error");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(t("departments.deleteConfirm", { name: item.name }))) return;
    try {
      await deleteDepartment(item.id, token, tenant);
      showSnackbar(t("departments.deleted"), "success");
      await loadData();
    } catch (err) {
      showSnackbar(err?.message || t("departments.deleteError"), "error");
    }
  };

  const handleUserDepartments = async (user, selectedDepartments) => {
    try {
      await updateUserDepartments(user.id, selectedDepartments.map((item) => item.id), token, tenant);
      showSnackbar(t("departments.userDepartmentsUpdated"), "success");
      await loadData();
    } catch (err) {
      showSnackbar(err?.message || t("departments.userDepartmentsError"), "error");
    }
  };

  if (tenantType !== "SALE_CONGRESSI") {
    return (
      <SidebarLayout className="admin-responsive-page">
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Alert severity="info">{t("departments.conferenceOnly")}</Alert>
        </Box>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout className="admin-responsive-page">
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={900}>{t("departments.title")}</Typography>
            <Typography color="text.secondary">
              {t("departments.subtitle")}
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            {t("departments.newDepartment")}
          </Button>
        </Stack>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        <Grid container spacing={2}>
          <Grid item xs={12} lg={6}>
            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight={900}>{t("departments.registryTitle")}</Typography>
                <Typography variant="body2" color="text.secondary">{t("departments.registrySubtitle")}</Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("departments.department")}</TableCell>
                    <TableCell>{t("departments.status")}</TableCell>
                    <TableCell>{t("departments.users")}</TableCell>
                    <TableCell align="right">{t("departments.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {departments.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Typography fontWeight={900}>{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.description || t("departments.noDescription")}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={item.active ? "success" : "default"} label={item.active ? t("common.status.active") : t("common.status.inactive")} />
                      </TableCell>
                      <TableCell>{item._count?.users || 0}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => openEdit(item)}><EditIcon /></IconButton>
                        <IconButton color="error" onClick={() => handleDelete(item)}><DeleteIcon /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && departments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>{t("departments.empty")}</TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Paper variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
              <Typography variant="h6" fontWeight={900}>{t("departments.usersTitle")}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("departments.usersSubtitle")}
              </Typography>
              <Stack spacing={2}>
                {users.map((user) => {
                  const selected = (user.departments || []).map((entry) => entry.department).filter(Boolean);
                  return (
                    <Paper key={user.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                          <Typography fontWeight={900}>{userLabel(user, t)}</Typography>
                          <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <Autocomplete
                            multiple
                            options={departments.filter((item) => item.active !== false || selected.some((sel) => sel.id === item.id))}
                            value={selected}
                            onChange={(_, value) => handleUserDepartments(user, value)}
                            getOptionLabel={(option) => option?.name || ""}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            renderInput={(params) => <TextField {...params} label={t("departments.assignedDepartments")} />}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  );
                })}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{editing ? t("departments.editDepartment") : t("departments.newDepartment")}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label={t("departments.departmentName")}
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                label={t("departments.description")}
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              />
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  checked={form.active}
                  onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
                />
                <Typography fontWeight={800}>{t("departments.departmentActive")}</Typography>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>{t("common.actions.cancel")}</Button>
            <Button variant="contained" onClick={handleSave}>{t("common.actions.save")}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SidebarLayout>
  );
}
